import _ from 'lodash';
import {
  getDataWithRetry,
  mapPagedResult,
  sendWithoutBody,
} from '../fetchUtils';
import { getDefaultError } from '../../utils';
import {
  isValidExercisesResponse,
  isValidExerciseResponse,
} from '../apiValidators';
import { apiBaseUrl } from '../apiConsts';
import { isRecord } from '../../utils/typeUtils';

import type { Exercise, WithOptionalId, PagedResponse } from '../../types';
import type { FileResponse } from '../apiTypes';
import type { FetchResult, FetchSuccess } from '../fetchUtils';

type UploadResponse = Record<string, string>;

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

const isUploadResponse = (data: unknown): data is UploadResponse => {
  return (
    isRecord(data) &&
    Object.values(data).every((value) => typeof value === 'string')
  );
};

export const fetchExerciseById = async (
  id: string,
): Promise<FetchResult<Exercise | null>> => {
  return getDataWithRetry<Exercise | null>(
    `${apiBaseUrl}exercises/${id}`,
    {},
    isValidExerciseResponse,
  );
};

export const getSourceExercises = async (): Promise<
  FetchResult<Exercise[]>
> => {
  const response = await getDataWithRetry<PagedResponse<Exercise>>(
    `${apiBaseUrl}exercises`,
    {},
    isValidExercisesResponse,
  );
  return mapPagedResult(response);
};

// NOTE: We call the per-trainer endpoint (not merged) because we only need the
// personalized exercises each coach authored. Global exercises are stored
// separately and stitched together with trainer data client-side.
export const getTrainerExercises = async (
  trainerIds: string[],
): Promise<FetchResult<Exercise[]>> => {
  if (!trainerIds.length) {
    return createSuccessResult<Exercise[]>([]);
  }
  const responses = await Promise.all(
    trainerIds.map((trainerId) =>
      getDataWithRetry<PagedResponse<Exercise>>(
        `${apiBaseUrl}exercises/trainer/${trainerId}?page=0&size=1000&sort=name`,
        {},
        isValidExercisesResponse,
      ),
    ),
  );
  const failure = responses.find((response) => !response.success);
  if (failure && !failure.success) {
    return {
      success: false,
      data: null,
      error:
        failure.error || getDefaultError('Failed to fetch trainer exercises'),
      status: failure.status,
      durationMs: failure.durationMs,
    };
  }
  const successfulResponses = responses as FetchSuccess<
    PagedResponse<Exercise>
  >[];
  const mergedExercises = successfulResponses
    .flatMap((response) => response.data.content ?? [])
    .filter(Boolean);
  return createSuccessResult<Exercise[]>(mergedExercises);
};

const mutateExerciseMuscle = async (
  exerciseId: string,
  muscleGroupType: string,
  action: 'associate' | 'unassociate',
  ids: string[],
): Promise<FetchResult<null>> => {
  if (!ids.length) {
    return createSuccessResult<null>(null);
  }
  return sendWithoutBody(
    `${apiBaseUrl}exercises/${exerciseId}/${action}/${muscleGroupType}`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(ids),
    },
  );
};

export const updateExerciseData = async (
  oldExerciseData: Exercise,
  newExerciseData: Exercise,
): Promise<FetchResult<Exercise | null>> => {
  if (!newExerciseData?.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No exercise id provided'),
    };
  }

  const updateResponse = await getDataWithRetry<Exercise | null>(
    `${apiBaseUrl}exercises`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'PUT',
      body: JSON.stringify(newExerciseData),
    },
    isValidExerciseResponse,
  );
  if (!updateResponse.success || !updateResponse.data) {
    return updateResponse;
  }

  const primaryMuscleIdsToUnassociate = _.difference(
    oldExerciseData.primaryMuscleGroupIds,
    newExerciseData.primaryMuscleGroupIds,
  );
  const primaryMuscleIdsToAssociate = _.difference(
    newExerciseData.primaryMuscleGroupIds,
    oldExerciseData.primaryMuscleGroupIds,
  );
  const secondaryMuscleIdsToUnassociate = _.difference(
    oldExerciseData.secondaryMuscleGroupIds,
    newExerciseData.secondaryMuscleGroupIds,
  );
  const secondaryMuscleIdsToAssociate = _.difference(
    newExerciseData.secondaryMuscleGroupIds,
    oldExerciseData.secondaryMuscleGroupIds,
  );
  const associationSteps: Array<Promise<FetchResult<null>>> = [
    mutateExerciseMuscle(
      newExerciseData.id,
      'primary-muscles',
      'unassociate',
      primaryMuscleIdsToUnassociate,
    ),
    mutateExerciseMuscle(
      newExerciseData.id,
      'primary-muscles',
      'associate',
      primaryMuscleIdsToAssociate,
    ),
    mutateExerciseMuscle(
      newExerciseData.id,
      'secondary-muscles',
      'unassociate',
      secondaryMuscleIdsToUnassociate,
    ),
    mutateExerciseMuscle(
      newExerciseData.id,
      'secondary-muscles',
      'associate',
      secondaryMuscleIdsToAssociate,
    ),
  ];

  for (const step of associationSteps) {
    const result = await step;
    if (!result.success) {
      return result as FetchResult<Exercise | null>;
    }
  }
  return updateResponse;
};

export const createPersonalizedExercise = async (
  exercise: WithOptionalId<Exercise>,
): Promise<FetchResult<Exercise | null>> => {
  if (exercise.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('cannot create an exercise with id'),
    };
  }
  return getDataWithRetry(
    `${apiBaseUrl}exercises/personalized`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(exercise),
    },
    isValidExerciseResponse,
  );
};

export const createGlobalExercise = async (
  exercise: WithOptionalId<Exercise>,
): Promise<FetchResult<Exercise | null>> => {
  if (exercise.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('cannot create an exercise with id'),
    };
  }
  return getDataWithRetry(
    `${apiBaseUrl}exercises`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(exercise),
    },
    isValidExerciseResponse,
  );
};

export const deleteExercise = async (
  exerciseId: string,
): Promise<FetchResult<null>> => {
  if (!exerciseId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('exerciseId is required'),
    };
  }
  return sendWithoutBody(`${apiBaseUrl}exercises/${exerciseId}/remove`, {
    method: 'PUT',
  });
};

export const uploadExerciseImages = async (
  files: File[],
  trainerId: string,
): Promise<FetchResult<FileResponse[]>> => {
  if (!files?.length || !trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('wrong parameters in uploadImage'),
    };
  }
  const filesFormData = new FormData();
  files.forEach((file) => {
    filesFormData.append('files', file);
  });
  const response = await getDataWithRetry<UploadResponse>(
    `${apiBaseUrl}exercises/uploadBulkImages?trainerId=${trainerId}&compress=true`,
    {
      mode: 'cors',
      method: 'POST',
      body: filesFormData,
    },
    isUploadResponse,
  );
  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error:
        response.error || getDefaultError('Failed to upload exercise images'),
      status: response.status,
      durationMs: response.durationMs,
    };
  }
  const responseData = Object.entries(response.data)
    .map(([fileName, fileUrl]) => {
      if (!fileName || !fileUrl) {
        return null;
      }
      return {
        fileName,
        fileUrl,
      };
    })
    .filter((item): item is FileResponse => item !== null);
  return createSuccessResult<FileResponse[]>(responseData);
};

export const uploadExerciseVideos = async (
  files: File[],
  trainerId: string,
): Promise<FetchResult<FileResponse[]>> => {
  if (!files?.length || !trainerId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('wrong parameters in uploadExerciseVideos'),
    };
  }
  const filesFormData = new FormData();
  files.forEach((file) => {
    filesFormData.append('files', file);
  });
  const response = await getDataWithRetry<UploadResponse>(
    `${apiBaseUrl}exercises/uploadBulkVideos?trainerId=${trainerId}&compress=true`,
    {
      mode: 'cors',
      method: 'POST',
      body: filesFormData,
    },
    isUploadResponse,
  );
  if (!response.success || !response.data) {
    return {
      success: false,
      data: null,
      error:
        response.error || getDefaultError('Failed to upload exercise videos'),
      status: response.status,
      durationMs: response.durationMs,
    };
  }
  const responseData = Object.entries(response.data)
    .map(([fileName, fileUrl]) => {
      if (!fileName || !fileUrl) {
        return null;
      }
      return {
        fileName,
        fileUrl,
      };
    })
    .filter((item): item is FileResponse => item !== null);
  return createSuccessResult<FileResponse[]>(responseData);
};
